import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

export type SwitchTypeValue = "Combined Switch" | "Supplier Switch";

interface SwitchTypeGateProps {
  onSelect: (value: SwitchTypeValue) => void;
  onClose: () => void;
}

const NewCustomerIcon = () => (
  <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
    />
  </svg>
);

const ExistingContractIcon = () => (
  <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.8}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const SwitchTypeCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  continueLabel: string;
  onContinue: () => void;
}> = ({ icon, title, description, continueLabel, onContinue }) => (
  <div className="flex flex-1 flex-col items-center text-center bg-gray-50 dark:bg-primary-800 border-[2px] border-gray-100 dark:border-primary-700 hover:border-primary-500 transition-colors rounded-lg p-8 min-h-full">
    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-50 dark:bg-primary-800 mb-6">
      {icon}
    </div>
    <h2 className="text-primary-600 text-lg font-semibold mb-3 min-h-[3.5rem] flex items-center">{title}</h2>
    <p className="text-primary-500 font-normal text-sm mb-8 flex-1">{description}</p>
    <button
      onClick={onContinue}
      className="w-full py-3 px-4 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
    >
      {continueLabel}
    </button>
  </div>
);

export const SwitchTypeGate: React.FC<SwitchTypeGateProps> = ({ onSelect, onClose }) => {
  const { t } = useTranslation("addservice");

  // Closeable modal: X button, backdrop click, and ESC all hide it WITHOUT selecting a
  // switchType (the wizard stays on the current step). Selection remains required to
  // advance — every attempt to proceed re-opens this modal (see AddService.tsx).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="switch-type-question"
      onClick={onClose}
    >
      <div
        className="relative bg-white dark:bg-primary-900 rounded-lg shadow-xl w-full max-w-3xl mx-4 p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-primary-400 hover:text-primary-600"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="mb-8 text-center">
          <h1 id="switch-type-question" className="text-primary-600 text-2xl font-semibold">
            {t("switchType.question")}
          </h1>
        </div>

        <div className="flex flex-col md:flex-row gap-6 items-stretch">
          <SwitchTypeCard
            icon={<NewCustomerIcon />}
            title={t("switchType.newCustomerTitle")}
            description={t("switchType.newCustomerDesc")}
            continueLabel={t("form.continue")}
            onContinue={() => onSelect("Combined Switch")}
          />
          <SwitchTypeCard
            icon={<ExistingContractIcon />}
            title={t("switchType.existingContractTitle")}
            description={t("switchType.existingContractDesc")}
            continueLabel={t("form.continue")}
            onContinue={() => onSelect("Supplier Switch")}
          />
        </div>

        <div className="mt-8 bg-primary-50 dark:bg-primary-800 rounded-lg p-6">
          <h3 className="text-primary-600 text-sm font-semibold mb-2">{t("switchType.whyTitle")}</h3>
          <p className="text-primary-500 font-normal text-sm">{t("switchType.whyBody")}</p>
        </div>
      </div>
    </div>
  );
};
